import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  UserPlus, 
  Store, 
  FileText, 
  MapPin, 
  Smartphone, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const steps = [
  {
    title: "1. Regístrate en la Plataforma",
    description: "Crea tu cuenta personal usando tu correo o Google. Necesitas estar identificado para que podamos vincular tu tienda a tu perfil.",
    icon: <UserPlus className="h-10 w-10 text-blue-500" />,
    image: "https://picsum.photos/seed/reg/800/400"
  },
  {
    title: "2. Datos de tu Marca",
    description: "Dale un nombre a tu tienda, sube tu logo y escribe una descripción atractiva que cuente qué hace especial a tu negocio.",
    icon: <Store className="h-10 w-10 text-primary" />,
    image: "https://picsum.photos/seed/store/800/400"
  },
  {
    title: "3. Tipo de Negocio",
    description: "¿Vendes productos físicos o prestas servicios profesionales? Puedes seleccionar ambos si lo necesitas.",
    icon: <Settings className="h-10 w-10 text-orange-500" />,
    image: "https://picsum.photos/seed/biz/800/400"
  },
  {
    title: "4. Ubicación y Contacto",
    description: "Dinos de qué país y ciudad eres para que los clientes cercanos puedan encontrarte. No olvides tu WhatsApp, ¡es tu canal de ventas!",
    icon: <MapPin className="h-10 w-10 text-red-500" />,
    image: "https://picsum.photos/seed/map/800/400"
  },
  {
    title: "5. ¡Lanza y Vende!",
    description: "Una vez creada, accede a tu Panel de Vendedor para subir tus primeros productos y empezar a recibir pedidos por WhatsApp.",
    icon: <CheckCircle2 className="h-10 w-10 text-green-500" />,
    image: "https://picsum.photos/seed/launch/800/400"
  }
];

export function TutorialGuide() {
  const [currentStep, setCurrentStep] = useState(0);

  const next = () => setCurrentStep((s) => (s + 1) % steps.length);
  const prev = () => setCurrentStep((s) => (s - 1 + steps.length) % steps.length);

  return (
    <Card className="overflow-hidden border-none shadow-none bg-transparent">
      <CardContent className="p-0">
        <div className="relative aspect-[21/9] w-full overflow-hidden rounded-3xl bg-muted mb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0"
            >
              <img 
                src={steps[currentStep].image} 
                className="w-full h-full object-cover opacity-40 blur-sm"
                alt="background"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 flex items-center justify-center p-8 md:p-12">
                <div className="grid md:grid-cols-[100px_1fr] gap-8 items-center max-w-4xl w-full">
                  <div className="flex justify-center">
                    <div className="h-20 w-20 rounded-2xl bg-background shadow-xl flex items-center justify-center">
                      {steps[currentStep].icon}
                    </div>
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl md:text-4xl font-bold mb-4">{steps[currentStep].title}</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {steps[currentStep].description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {steps.map((_, i) => (
              <div 
                key={i} 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep ? "w-8 bg-primary" : "w-2 bg-primary/20"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center gap-4">
          <Button variant="outline" size="lg" onClick={prev} className="rounded-2xl">
            <ChevronLeft className="mr-2 h-5 w-5" /> Anterior
          </Button>
          <div className="text-sm font-medium text-muted-foreground">
            Paso {currentStep + 1} de {steps.length}
          </div>
          <Button size="lg" onClick={next} className="rounded-2xl">
            Siguiente <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
